/**
 * EvaluationMetricsCard Component
 *
 * Displays aggregate evaluation metrics for a batch including pass rate,
 * status counts, and common failure reasons.
 */

import React from 'react';

interface EvaluationMetricsCardProps {
  totalJobs: number;
  passCount: number;
  failCount: number;
  errorCount: number;
  naCount: number;
  passRate: number;
  commonFailureReasons: Array<{ reason: string; count: number }>;
}

export const EvaluationMetricsCard: React.FC<EvaluationMetricsCardProps> = ({
  totalJobs,
  passCount,
  failCount,
  errorCount,
  naCount,
  passRate,
  commonFailureReasons,
}) => {
  const evaluatedJobs = passCount + failCount;
  const hasEvaluations = evaluatedJobs > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Evaluation Metrics</h2>

      {!hasEvaluations && (
        <p className="text-sm text-gray-500">
          No evaluations available for this batch
        </p>
      )}

      {hasEvaluations && (
        <>
          {/* Statistics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {/* Total Evaluated */}
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{evaluatedJobs}</p>
              <p className="text-xs text-gray-500 mt-1">Total Evaluated</p>
            </div>

            {/* Pass Count */}
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{passCount}</p>
              <p className="text-xs text-gray-500 mt-1">
                Pass ({evaluatedJobs > 0 ? ((passCount / evaluatedJobs) * 100).toFixed(1) : 0}%)
              </p>
            </div>

            {/* Fail Count */}
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{failCount}</p>
              <p className="text-xs text-gray-500 mt-1">
                Fail ({evaluatedJobs > 0 ? ((failCount / evaluatedJobs) * 100).toFixed(1) : 0}%)
              </p>
            </div>

            {/* Error Count */}
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{errorCount}</p>
              <p className="text-xs text-gray-500 mt-1">Error</p>
            </div>

            {/* N/A Count */}
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-600">{naCount}</p>
              <p className="text-xs text-gray-500 mt-1">N/A</p>
            </div>
          </div>

          {/* Pass Rate Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Pass Rate</span>
              <span className="text-sm font-bold text-gray-900">
                {(passRate * 100).toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-green-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${passRate * 100}%` }}
                role="progressbar"
                aria-valuenow={passRate * 100}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Pass rate: ${(passRate * 100).toFixed(1)}%`}
              ></div>
            </div>
          </div>

          {/* Common Failure Reasons */}
          {commonFailureReasons && commonFailureReasons.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Common Failure Reasons
              </h3>
              <ul className="space-y-2">
                {commonFailureReasons.slice(0, 5).map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start text-sm bg-gray-50 rounded-md p-2 border border-gray-200"
                  >
                    <span className="flex-shrink-0 text-gray-400 mr-2">
                      {index + 1}.
                    </span>
                    <span className="flex-1 text-gray-900">{item.reason}</span>
                    <span className="flex-shrink-0 text-gray-500 ml-2">
                      ({item.count} {item.count === 1 ? 'job' : 'jobs'})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};
